import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import api from '../../services/api';
import { FiEye, FiTrash2 } from 'react-icons/fi';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.h2`
  color: #fff;
  font-size: 24px;
  margin: 0;
`;

const Table = styled.div`
  background: #0f0f0f;
  border: 1px solid #333;
  border-radius: 12px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr 1fr 1.5fr 0.8fr;
  gap: 20px;
  padding: 16px;
  background: #1a1a1a;
  border-bottom: 1px solid #333;
  font-weight: 600;
  color: #999;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr 0.8fr;

    & > :nth-child(4) {
      display: none;
    }
  }
`;

const TableRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1.5fr 1fr 1.5fr 0.8fr;
  gap: 20px;
  padding: 16px;
  border-bottom: 1px solid #333;
  align-items: center;
  transition: all 0.3s ease;

  &:hover {
    background: #1a1a1a;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr 0.8fr;

    & > :nth-child(4) {
      display: none;
    }
  }
`;

const CustomerName = styled.p`
  color: #fff;
  font-weight: 600;
  margin: 0;
  font-size: 14px;
`;

const Email = styled.p`
  color: #999;
  margin: 0;
  font-size: 13px;
`;

const Phone = styled.p`
  color: #fff;
  margin: 0;
  font-size: 14px;
`;

const Address = styled.p`
  color: #999;
  margin: 0;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  padding: 6px 10px;
  background: none;
  border: 1px solid #333;
  border-radius: 4px;
  color: #2196f3;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  transition: all 0.3s ease;

  &:hover {
    background: #2196f3;
    color: #fff;
    border-color: #2196f3;
  }
`;

const DeleteButton = styled(ActionButton)`
  color: #f44336;

  &:hover {
    background: #f44336;
  }
`;

const EmptyState = styled.div`
  padding: 40px;
  text-align: center;
  color: #999;
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #0f0f0f;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 30px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  color: #fff;
  margin-bottom: 20px;
`;

const InfoGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  color: #999;
  font-size: 12px;
  text-transform: uppercase;
  display: block;
  margin-bottom: 6px;
`;

const Value = styled.p`
  color: #fff;
  margin: 0;
  font-size: 14px;
`;

const OrderHistory = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #333;
`;

const OrderItem = styled.div`
  padding: 12px;
  background: #1a1a1a;
  border-radius: 6px;
  margin-bottom: 8px;
  font-size: 13px;
  color: #999;
`;

const CloseButton = styled.button`
  padding: 10px 20px;
  background: #666;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  margin-top: 20px;
  width: 100%;
  transition: all 0.3s ease;

  &:hover {
    background: #555;
  }
`;

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await api.adminGetCustomers();
      if (response && response.data) {
        setCustomers(response.data);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setCustomers([]);
      setLoading(false);
    }
  };

  const handleViewDetails = async (customer) => {
    setSelectedCustomer(customer);
    try {
      const response = await api.adminGetCustomer(customer.id);
      if (response && response.data) {
        setCustomerOrders(response.data.orders || []);
      }
    } catch (err) {
      console.error('Error fetching customer orders:', err);
      setCustomerOrders([]);
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        const response = await api.adminDeleteCustomer(customerId);
        if (response && response.success) {
          setCustomers(customers.filter(c => c.id !== customerId));
        }
      } catch (err) {
        console.error('Error deleting customer:', err);
        alert(err.response?.data?.message || 'Failed to delete customer');
      }
    }
  };

  if (loading) {
    return <Container>Loading customers...</Container>;
  }

  return (
    <Container>
      <Title>Customers Management</Title>

      {customers.length > 0 ? (
        <Table>
          <TableHeader>
            <div>Name</div>
            <div>Email</div>
            <div>Phone</div>
            <div>Address</div>
            <div>Actions</div>
          </TableHeader>
          {customers.map((customer, index) => (
            <TableRow
              key={customer.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <CustomerName>{customer.name}</CustomerName>
              <Email>{customer.email}</Email>
              <Phone>{customer.phone}</Phone>
              <Address>{customer.address}</Address>
              <Actions>
                <ActionButton onClick={() => handleViewDetails(customer)}>
                  <FiEye /> View
                </ActionButton>
                <DeleteButton onClick={() => handleDeleteCustomer(customer.id)}>
                  <FiTrash2 /> Delete
                </DeleteButton>
              </Actions>
            </TableRow>
          ))}
        </Table>
      ) : (
        <EmptyState>No customers found</EmptyState>
      )}

      {selectedCustomer && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setSelectedCustomer(null)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Customer Details</ModalTitle>
            <InfoGroup>
              <Label>Name</Label>
              <Value>{selectedCustomer.name}</Value>
            </InfoGroup>
            <InfoGroup>
              <Label>Email</Label>
              <Value>{selectedCustomer.email}</Value>
            </InfoGroup>
            <InfoGroup>
              <Label>Phone</Label>
              <Value>{selectedCustomer.phone}</Value>
            </InfoGroup>
            <InfoGroup>
              <Label>Address</Label>
              <Value>{selectedCustomer.address}</Value>
            </InfoGroup>
            <InfoGroup>
              <Label>Member Since</Label>
              <Value>
                {selectedCustomer.createdAt?.toDate?.().toLocaleDateString?.() || 'N/A'}
              </Value>
            </InfoGroup>

            {customerOrders.length > 0 && (
              <OrderHistory>
                <Label>Order History ({customerOrders.length})</Label>
                {customerOrders.map(order => (
                  <OrderItem key={order.id}>
                    Order #{order.id.slice(0, 8)} - {order.status} - ₹{order.totalAmount || 0}
                  </OrderItem>
                ))}
              </OrderHistory>
            )}

            <CloseButton onClick={() => setSelectedCustomer(null)}>Close</CloseButton>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default AdminCustomers;
